import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  ToggleButton,
  Slider,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  DirectionsCar as CarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AcUnit as AcIcon,
  Bluetooth as BluetoothIcon,
  Power as UsbIcon,
  Wifi as WifiIcon,
  EventSeat as ChairIcon,
  WbSunny as SunroofIcon,
  Navigation as GpsIcon,
  Videocam as CameraIcon,
  Whatshot as HeatedIcon,
  Pets as PetIcon,
  SmokeFree as NoSmokingIcon,
  Accessible as AccessibleIcon,
  Info as InfoIcon,
  CloudUpload as UploadIcon,
  Description as FileIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  AccessTime as PendingIcon,
  PhotoCamera as PhotoCameraIcon,
  AddAPhoto as AddPhotoIcon,
} from '@mui/icons-material';
import { useLoading } from '../../contexts/LoadingContext';
import apiService from '../../services/apiService';

// Canadian provinces and their document types
const CANADIAN_PROVINCES = [
  { code: 'BC', name: 'British Columbia', documentType: 'INTEGRATED', provider: 'ICBC' },
  { code: 'MB', name: 'Manitoba', documentType: 'INTEGRATED', provider: 'MPI' },
  { code: 'SK', name: 'Saskatchewan', documentType: 'INTEGRATED', provider: 'SGI' },
  { code: 'ON', name: 'Ontario', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer (Pink Card)', registrationProvider: 'ServiceOntario (Vehicle Permit)' },
  { code: 'AB', name: 'Alberta', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer (Pink Card)', registrationProvider: 'Registry Agent' },
  { code: 'NS', name: 'Nova Scotia', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer', registrationProvider: 'Registry of Motor Vehicles' },
  { code: 'NB', name: 'New Brunswick', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer', registrationProvider: 'Service New Brunswick' },
  { code: 'NL', name: 'Newfoundland & Labrador', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer', registrationProvider: 'Service NL' },
  { code: 'PE', name: 'Prince Edward Island', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer', registrationProvider: 'Access PEI' },
  { code: 'YT', name: 'Yukon', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer', registrationProvider: 'Territorial Government' },
  { code: 'NT', name: 'Northwest Territories', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer', registrationProvider: 'Territorial Government' },
  { code: 'NU', name: 'Nunavut', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer', registrationProvider: 'Territorial Government' },
  { code: 'QC', name: 'Quebec', documentType: 'SEPARATE', insuranceProvider: 'Private Insurer (Property Damage)', registrationProvider: 'SAAQ (Registration & Bodily Injury)' },
];

// Vehicle data constants
const VEHICLE_COLORS = [
  'White', 'Black', 'Silver', 'Gray',
  'Red', 'Blue', 'Green', 'Yellow',
  'Orange', 'Brown', 'Beige', 'Purple'
];

const VEHICLE_FEATURES = [
  { id: 'air_conditioning', label: 'Air Conditioning', icon: <AcIcon /> },
  { id: 'bluetooth', label: 'Bluetooth', icon: <BluetoothIcon /> },
  { id: 'usb_charging', label: 'USB Charging', icon: <UsbIcon /> },
  { id: 'wifi', label: 'Wi-Fi', icon: <WifiIcon /> },
  { id: 'leather_seats', label: 'Leather Seats', icon: <ChairIcon /> },
  { id: 'sunroof', label: 'Sunroof', icon: <SunroofIcon /> },
  { id: 'gps', label: 'GPS Navigation', icon: <GpsIcon /> },
  { id: 'backup_camera', label: 'Backup Camera', icon: <CameraIcon /> },
  { id: 'heated_seats', label: 'Heated Seats', icon: <HeatedIcon /> },
  { id: 'pet_friendly', label: 'Pet Friendly', icon: <PetIcon /> },
  { id: 'non_smoking', label: 'Non-Smoking', icon: <NoSmokingIcon /> },
  { id: 'wheelchair_accessible', label: 'Wheelchair Accessible', icon: <AccessibleIcon /> },
];

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://rideclubnet.com/api'
  : 'http://localhost:3001/api';
const FILE_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://rideclubnet.com'
  : 'http://localhost:3001';

interface VehicleMake {
  id: string;
  name: string;
}

interface VehicleModel {
  id: string;
  name: string;
  makeId: string;
  type: string;
  defaultSeats: number;
}

interface VehicleDocument {
  id: string;
  type: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'RESUBMISSION_REQUIRED';
  fileUrl: string;
  fileName?: string;
  uploadedAt?: string;
  reviewNotes?: string | null;
}

interface Vehicle {
  id: string;
  make: VehicleMake;
  model: VehicleModel;
  year: number;
  color: string;
  licensePlate: string;
  type: string;
  seats: number;
  features: string[];
  photos?: string[];
  description?: string;
  verificationStatus?: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'NEEDS_UPDATE';
  adminNote?: string;
  documents?: VehicleDocument[];
}

interface VehicleForm {
  makeId: string;
  makeName: string;
  modelId: string;
  modelName: string;
  customMake?: string;
  customModel?: string;
  customType?: string;
  year: string;
  color: string;
  licensePlate: string;
  type: string;
  seats: number;
  features: string[];
  description: string;
}

const VehicleManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setLoading } = useLoading();
  const location = useLocation();
  const navigate = useNavigate();
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [manualMode, setManualMode] = useState(true); // Manual entry by default (no OCR)
  const [isCustomVehicle, setIsCustomVehicle] = useState(false);

  const [vehicleMakes, setVehicleMakes] = useState<VehicleMake[]>([]);
  const [vehicleModels, setVehicleModels] = useState<VehicleModel[]>([]);
  const [docFormat, setDocFormat] = useState<'COMBINED' | 'SEPARATE'>('COMBINED');
  const [documents, setDocuments] = useState({
    vehicleRegistration: null as File | null,
    insurance: null as File | null,
    integratedDocument: null as File | null,
  });
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);
  const [extractingDocInfo, setExtractingDocInfo] = useState<string | null>(null);

  // Extracted data from AI
  const [extractedDataDialogOpen, setExtractedDataDialogOpen] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [editableExtractedData, setEditableExtractedData] = useState<any>(null);
  const [dataWasModifiedByUser, setDataWasModifiedByUser] = useState(false);
  const [isConfirmingVehicle, setIsConfirmingVehicle] = useState(false); // Prevent double-clicks

  // Modal-specific alert message for document upload errors
  const [modalAlertMessage, setModalAlertMessage] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; message: string } | null>(null);

  const [vehicleForm, setVehicleForm] = useState<VehicleForm>({
    makeId: '',
    makeName: '',
    modelId: '',
    modelName: '',
    year: '',
    color: '',
    licensePlate: '',
    type: 'SEDAN',
    seats: 5,
    features: [],
    description: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [resubmitting, setResubmitting] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; message: string } | null>(null);

  // Vehicle photo state
  const [vehiclePhoto, setVehiclePhoto] = useState<File | null>(null);
  const [vehiclePhotoPreview, setVehiclePhotoPreview] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    loadVehicles();
    loadVehicleMakes();

    if (location.state?.showAddVehicleModal) {
      handleAddVehicle();
      // Clear location state so the modal doesn't pop up again on refresh
      navigate('.', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Helper to check if province uses integrated documents
  const isIntegratedProvince = (provinceCode: string) => {
    return ['BC', 'MB', 'SK'].includes(provinceCode);
  };

  // Get province info
  const getProvinceInfo = (provinceCode: string) => {
    return CANADIAN_PROVINCES.find(p => p.code === provinceCode);
  };

  // Fuzzy string matching using Levenshtein distance
  const levenshteinDistance = (str1: string, str2: string): number => {
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();
    const costs: number[] = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) {
        costs[s2.length] = lastValue;
      }
    }
    return costs[s2.length];
  };

  // Find best matching string from list
  const findBestMatch = (input: string, options: string[]): string | null => {
    if (!input || options.length === 0) return null;

    let bestMatch = null;
    let bestDistance = Infinity;
    const threshold = 3; // Allow up to 3 character differences

    for (const option of options) {
      const distance = levenshteinDistance(input, option);
      if (distance < bestDistance && distance <= threshold) {
        bestDistance = distance;
        bestMatch = option;
      }
    }

    return bestMatch;
  };

  // Vehicle verification status helper
  const getVerificationStatusDisplay = (status?: string) => {
    switch (status) {
      case 'VERIFIED':
        return { label: 'Verified', color: 'success' as const, icon: <CheckCircleIcon fontSize="small" /> };
      case 'REJECTED':
        return { label: 'Rejected', color: 'error' as const, icon: <ErrorIcon fontSize="small" /> };
      case 'NEEDS_UPDATE':
        return { label: 'Update Required', color: 'warning' as const, icon: <WarningIcon fontSize="small" /> };
      case 'PENDING':
      default:
        return { label: 'Pending Review', color: 'info' as const, icon: <PendingIcon fontSize="small" /> };
    }
  };

  const handleResubmitVehicle = async (vehicleId: string) => {
    setResubmitting(vehicleId);
    try {
      const response = await apiService.post(`/vehicles/${vehicleId}/resubmit`);
      if (response.success) {
        setAlertMessage({ type: 'success', message: 'Vehicle resubmitted for verification. You will be notified once reviewed.' });
        loadVehicles();
      } else {
        setAlertMessage({ type: 'error', message: response.message || 'Failed to resubmit vehicle' });
      }
    } catch (error: any) {
      console.error('Resubmit vehicle error:', error);
      setAlertMessage({ type: 'error', message: error.response?.data?.message || 'Failed to resubmit vehicle' });
    } finally {
      setResubmitting(null);
    }
  };

  const loadVehicles = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get('/vehicles/my-vehicles');
      if (response.success) {
        setVehicles(response.data.vehicles || []);
      }
    } catch (error) {
      console.error('Load vehicles error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadVehicleMakes = async () => {
    try {
      const response = await apiService.get('/vehicles/makes');
      if (response.success) {
        setVehicleMakes(response.data.makes || []);
      }
    } catch (error) {
      console.error('Load vehicle makes error:', error);
    }
  };

  const loadVehicleModels = async (makeId: string): Promise<VehicleModel[]> => {
    try {
      const response = await apiService.get(`/vehicles/makes/${makeId}/models`);
      if (response.success) {
        const models = response.data.models || [];
        setVehicleModels(models);
        return models;
      }
    } catch (error) {
      console.error('Load vehicle models error:', error);
    }
    return [];
  };

  const resetForm = () => {
    setVehicleForm({
      makeId: '',
      makeName: '',
      modelId: '',
      modelName: '',
      year: '',
      color: '',
      licensePlate: '',
      type: 'SEDAN',
      seats: 5,
      features: [],
      description: '',
    });
    setVehicleModels([]);
    setErrors({});
    setDocFormat('COMBINED');
    setDocuments({
      vehicleRegistration: null as File | null,
      insurance: null as File | null,
      integratedDocument: null as File | null,
    });
    setVehiclePhoto(null);
    setVehiclePhotoPreview(null);
    setManualMode(false);
  };

  // Vehicle photo handlers
  const handleVehiclePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (file.size > maxSize) {
        setAlertMessage({ type: 'error', message: 'Photo size must be less than 10MB' });
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setAlertMessage({ type: 'error', message: 'Only JPEG, PNG, and WEBP images are allowed' });
        return;
      }

      setVehiclePhoto(file);
      setVehiclePhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleUploadVehiclePhoto = async (vehicleId: string) => {
    if (!vehiclePhoto) return;

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('vehiclePhoto', vehiclePhoto);

      const response = await apiService.uploadFile(`/vehicles/${vehicleId}/photo`, formData);

      if (response.success) {
        setAlertMessage({ type: 'success', message: 'Vehicle photo uploaded successfully!' });
        setVehiclePhoto(null);
        setVehiclePhotoPreview(null);
        loadVehicles();
      } else {
        setAlertMessage({ type: 'error', message: response.message || 'Failed to upload photo' });
      }
    } catch (error: any) {
      console.error('Upload vehicle photo error:', error);
      setAlertMessage({
        type: 'error',
        message: error.response?.data?.message || 'Failed to upload photo. It may contain inappropriate content.'
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeleteVehiclePhoto = async (vehicleId: string, photoUrl: string) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      const response = await apiService.delete(`/vehicles/${vehicleId}/photo?photoUrl=${encodeURIComponent(photoUrl)}`);

      if (response.success) {
        setAlertMessage({ type: 'success', message: 'Photo deleted successfully' });
        loadVehicles();
      } else {
        setAlertMessage({ type: 'error', message: response.message || 'Failed to delete photo' });
      }
    } catch (error: any) {
      console.error('Delete vehicle photo error:', error);
      setAlertMessage({ type: 'error', message: 'Failed to delete photo' });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (isCustomVehicle) {
      if (!vehicleForm.customMake?.trim()) {
        newErrors.makeId = 'Vehicle make is required';
      }
      if (!vehicleForm.customModel?.trim()) {
        newErrors.modelId = 'Vehicle model is required';
      }
    } else {
      if (!vehicleForm.makeId) {
        newErrors.makeId = 'Vehicle make is required';
      }
      if (!vehicleForm.modelId) {
        newErrors.modelId = 'Vehicle model is required';
      }
    }

    const year = parseInt(vehicleForm.year);
    if (!year || year < 1990 || year > new Date().getFullYear() + 1) {
      newErrors.year = 'Please enter a valid year';
    }

    if (!vehicleForm.color) {
      newErrors.color = 'Please select a color';
    }

    if (!vehicleForm.licensePlate.trim()) {
      newErrors.licensePlate = 'License plate is required';
    }

    if (vehicleForm.seats < 2 || vehicleForm.seats > 15) {
      newErrors.seats = 'Seating capacity must be between 2 and 15';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddVehicle = () => {
    resetForm();
    setEditingVehicle(null);
    setManualMode(true); // Default to manual entry
    setIsCustomVehicle(false);
    setModalAlertMessage(null); // Clear modal alert when opening
    setDialogOpen(true);
  };

  const handleEditVehicle = async (vehicle: Vehicle) => {
    setIsCustomVehicle(false);
    setVehicleForm({
      makeId: vehicle.make.id,
      makeName: vehicle.make.name,
      modelId: vehicle.model.id,
      modelName: vehicle.model.name,
      year: vehicle.year.toString(),
      color: vehicle.color,
      licensePlate: vehicle.licensePlate,
      type: vehicle.type,
      seats: vehicle.seats,
      features: vehicle.features || [],
      description: vehicle.description || '',
    });
    // Load models for the selected make
    await loadVehicleModels(vehicle.make.id);
    setEditingVehicle(vehicle);
    setDialogOpen(true);
  };

  const handleDeleteVehicle = (vehicle: Vehicle) => {
    setVehicleToDelete(vehicle);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteVehicle = async () => {
    if (!vehicleToDelete) return;

    setLoading(true, 'Deleting vehicle...');
    try {
      const response = await apiService.delete(`/vehicles/${vehicleToDelete.id}`);
      if (response.success) {
        setVehicles(prev => prev.filter(v => v.id !== vehicleToDelete.id));
        setDeleteDialogOpen(false);
        setVehicleToDelete(null);
      } else {
        alert(response.message || 'Failed to delete vehicle. Please try again.');
      }
    } catch (error: any) {
      console.error('Delete vehicle error:', error);
      alert(error.response?.data?.message || 'Failed to delete vehicle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVehicle = async () => {
    if (!validateForm()) return;

    setLoading(true, editingVehicle ? 'Updating vehicle...' : 'Adding vehicle...');
    try {
      const vehicleData = isCustomVehicle ? {
        customMake: vehicleForm.customMake || undefined,
        customModel: vehicleForm.customModel || undefined,
        customType: vehicleForm.type || undefined,
        year: parseInt(vehicleForm.year),
        color: vehicleForm.color,
        licensePlate: vehicleForm.licensePlate.trim().toUpperCase(),
        seats: vehicleForm.seats,
        features: vehicleForm.features,
      } : {
        makeId: vehicleForm.makeId || undefined,
        modelId: vehicleForm.modelId || undefined,
        year: parseInt(vehicleForm.year),
        color: vehicleForm.color,
        licensePlate: vehicleForm.licensePlate.trim().toUpperCase(),
        seats: vehicleForm.seats,
        features: vehicleForm.features,
      };

      let response;
      if (editingVehicle) {
        response = await apiService.put(`/vehicles/${editingVehicle.id}`, vehicleData);
      } else {
        response = await apiService.post('/vehicles', vehicleData);
      }

      if (response.success) {
        const vehicleId = editingVehicle?.id || response.data.vehicle?.id;

        // Upload vehicle photo if selected (for new vehicles)
        if (!editingVehicle && vehiclePhoto && vehicleId) {
          try {
            await handleUploadVehiclePhoto(vehicleId);
          } catch (photoError) {
            console.error('Vehicle photo upload error:', photoError);
          }
        }

        await loadVehicles();
        setDialogOpen(false);
        resetForm();
        setEditingVehicle(null);

        if (!editingVehicle) {
          setAlertMessage({
            type: 'success',
            message: '✅ Vehicle added successfully! You can now create and offer rides.'
          });
          // Refresh auth so driverStatus updates to APPROVED immediately
          try {
            const profileRes = await apiService.get('/auth/profile');
            if (profileRes.success) {
              const refreshedUser = (profileRes as any).data?.user || (profileRes as any).data;
              if (refreshedUser) {
                localStorage.setItem('rideclub_user', JSON.stringify(refreshedUser));
                window.dispatchEvent(new Event('user-data-refreshed'));
              }
            }
          } catch (refreshErr) {
            console.error('Failed to refresh user data:', refreshErr);
          }
        }
      } else {
        alert(response.message || 'Failed to save vehicle. Please try again.');
      }
    } catch (error: any) {
      console.error('Save vehicle error:', error);
      alert(error.response?.data?.message || 'Failed to save vehicle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field: keyof VehicleForm) => (event: any) => {
    const value = event.target ? event.target.value : event;
    setVehicleForm(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleFeature = (featureId: string) => {
    setVehicleForm(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const handleMakeChange = async (makeId: string, makeName: string) => {
    setVehicleForm(prev => ({
      ...prev,
      makeId,
      makeName,
      modelId: '',
      modelName: '',
    }));
    if (makeId) {
      await loadVehicleModels(makeId);
    } else {
      setVehicleModels([]);
    }
    if (errors.makeId) {
      setErrors(prev => ({ ...prev, makeId: '' }));
    }
  };

  const handleModelChange = (modelId: string, modelName: string, modelType: string, defaultSeats: number) => {
    setVehicleForm(prev => ({
      ...prev,
      modelId,
      modelName,
      type: modelType,
      seats: defaultSeats,
    }));
    if (errors.modelId) {
      setErrors(prev => ({ ...prev, modelId: '' }));
    }
  };

  // Handle confirming extracted data - Create vehicle directly
  const handleConfirmExtractedData = async () => {
    if (!editableExtractedData || isConfirmingVehicle) return; // Prevent double-clicks

    // Validate required fields
    if (!editableExtractedData.licensePlate || !editableExtractedData.color || !editableExtractedData.year) {
      setAlertMessage({
        type: 'error',
        message: 'Please fill in all required fields (License Plate, Color, Year)'
      });
      return;
    }

    setIsConfirmingVehicle(true); // Set loading state

    setLoading(true, 'Creating vehicle...');

    try {
      // Find matching make
      let makeId = '';
      let makeName = '';
      const matchingMake = vehicleMakes.find(m =>
        m.name.toLowerCase().includes(editableExtractedData.make.toLowerCase()) ||
        editableExtractedData.make.toLowerCase().includes(m.name.toLowerCase())
      );

      if (!matchingMake) {
        setAlertMessage({
          type: 'error',
          message: `Could not find matching vehicle make for "${editableExtractedData.make}". Please edit the make field.`
        });
        setLoading(false);
        setIsConfirmingVehicle(false);
        return;
      }

      makeId = matchingMake.id;
      makeName = matchingMake.name;

      // Load models for the selected make
      const models = await loadVehicleModels(makeId);

      // Find matching model
      let modelId = '';
      let modelName = '';
      let vehicleType = 'SEDAN';
      let defaultSeats = 5;

      if (editableExtractedData.model && models.length > 0) {
        // First try exact match (case-insensitive substring)
        let matchingModel = models.find(m =>
          m.name.toLowerCase().includes(editableExtractedData.model.toLowerCase()) ||
          editableExtractedData.model.toLowerCase().includes(m.name.toLowerCase())
        );

        // If no exact match, try fuzzy matching
        if (!matchingModel) {
          const modelNames = models.map(m => m.name);
          const bestMatchName = findBestMatch(editableExtractedData.model, modelNames);

          if (bestMatchName) {
            matchingModel = models.find(m => m.name === bestMatchName);

            // Show info message about fuzzy match
            setModalAlertMessage({
              type: 'info',
              message: `Did you mean "${bestMatchName}"? We found a close match for "${editableExtractedData.model}".`
            });
          }
        }

        if (matchingModel) {
          modelId = matchingModel.id;
          modelName = matchingModel.name;
          vehicleType = matchingModel.type;
          defaultSeats = matchingModel.defaultSeats;

          console.log(`✅ Model matched: "${editableExtractedData.model}" → "${modelName}"`);
        } else {
          setAlertMessage({
            type: 'error',
            message: `Could not find matching vehicle model "${editableExtractedData.model}" for ${makeName}. Please edit the model field.`
          });
          setLoading(false);
          setIsConfirmingVehicle(false);
          return;
        }
      } else {
        setAlertMessage({
          type: 'error',
          message: 'Please specify a vehicle model.'
        });
        setLoading(false);
        setIsConfirmingVehicle(false);
        return;
      }

      // Create vehicle with extracted data
      // If user modified the AI-extracted data, it needs admin verification
      // If user confirmed without changes, auto-approve
      const vehicleData = {
        makeId,
        modelId,
        year: parseInt(editableExtractedData.year),
        color: editableExtractedData.color,
        licensePlate: editableExtractedData.licensePlate.trim().toUpperCase(),
        seats: defaultSeats,
        features: [],
        aiExtracted: true,
        userModified: dataWasModifiedByUser,
      };

      console.log('📝 Creating vehicle with data:', vehicleData);
      console.log(`🔍 User modified data: ${dataWasModifiedByUser} → Expected status: ${dataWasModifiedByUser ? 'PENDING' : 'APPROVED'}`);

      const response = await apiService.post('/vehicles', vehicleData);

      if (response.success) {
        const vehicleId = response.data.vehicle?.id;
        const createdVehicle = response.data.vehicle;

        console.log('✅ Vehicle created successfully:', {
          id: vehicleId,
          licensePlate: createdVehicle?.licensePlate,
          verificationStatus: createdVehicle?.verificationStatus,
          make: createdVehicle?.make?.name,
          model: createdVehicle?.model?.name
        });

        if (vehicleId) {
          // Vehicle is auto-approved — no document upload required

          // Upload vehicle photo if selected
          if (vehiclePhoto && vehicleId) {
            try {
              await handleUploadVehiclePhoto(vehicleId);
            } catch (photoError) {
              console.error('Vehicle photo upload error:', photoError);
            }
          }

          // Refresh vehicle list to show updated verification status
          await loadVehicles();
        }

        // Close dialogs and reset
        setExtractedDataDialogOpen(false);
        setDialogOpen(false); // Close the main form dialog too
        setExtractedData(null);
        setEditableExtractedData(null);
        setDocuments({ vehicleRegistration: null, insurance: null, integratedDocument: null });
        setVehiclePhoto(null);
        setVehiclePhotoPreview(null);
        resetForm();

        // Reload vehicles list
        await loadVehicles();

        setAlertMessage({
          type: 'success',
          message: '✅ Vehicle added successfully! You can now create and offer rides.'
        });

        // Refresh auth so driverStatus updates to APPROVED immediately
        try {
          const profileRes = await apiService.get('/auth/profile');
          if (profileRes.success) {
            const refreshedUser = (profileRes as any).data?.user || (profileRes as any).data;
            if (refreshedUser) {
              localStorage.setItem('rideclub_user', JSON.stringify(refreshedUser));
              window.dispatchEvent(new Event('user-data-refreshed'));
            }
          }
        } catch (refreshErr) {
          console.error('Failed to refresh user data:', refreshErr);
        }

        // Reset modification flag
        setDataWasModifiedByUser(false);
      } else {
        setAlertMessage({
          type: 'error',
          message: response.message || 'Failed to create vehicle. Please try again.'
        });
      }
    } catch (error: any) {
      console.error('Error creating vehicle from extracted data:', error);
      setAlertMessage({
        type: 'error',
        message: error.response?.data?.message || 'Failed to create vehicle. Please try again.'
      });
    } finally {
      setLoading(false);
      setIsConfirmingVehicle(false); // Reset loading state
    }
  };

  // Handle editing extracted data in dialog
  const handleEditExtractedData = (field: string, value: string) => {
    setEditableExtractedData((prev: any) => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Check if user modified any data compared to original extracted data
      const wasModified = extractedData && Object.keys(newData).some(key => {
        return newData[key] !== extractedData[key];
      });

      setDataWasModifiedByUser(wasModified);
      return newData;
    });
  };

  const getFeatureDisplay = (featureId: string) => {
    const feature = VEHICLE_FEATURES.find(f => f.id === featureId);
    return feature ? { icon: feature.icon, label: feature.label } : null;
  };

  const findDoc = (vehicle: Vehicle, type: string) =>
    vehicle.documents?.find((d) => d.type === type);

  const getDocChipColor = (status?: VehicleDocument['status']) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'RESUBMISSION_REQUIRED':
        return 'warning';
      case 'PENDING':
      default:
        return 'warning';
    }
  };

  const handleDocumentUpload = async (vehicleId: string, docType: string, file: File | null) => {
    if (!file) return;
    setUploadingDocType(docType);
    setLoading(true, 'Uploading document...');
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('documentType', docType);
      formData.append('vehicleId', vehicleId);

      const response = await apiService.uploadFile('/documents/upload', formData);

      if (response.success) {
        await loadVehicles();
        setAlertMessage({
          type: 'info',
          message: 'Document uploaded. Vehicle is pending verification and related rides are hidden until admin approval.'
        });
      } else {
        setAlertMessage({ type: 'error', message: response.message || 'Failed to upload document' });
      }
    } catch (error: any) {
      console.error('Upload vehicle document error:', error);
      setAlertMessage({
        type: 'error',
        message: error.response?.data?.message || 'Failed to upload document'
      });
    } finally {
      setLoading(false);
      setUploadingDocType(null);
    }
  };

  const handleFileChange = (docType: 'vehicleRegistration' | 'insurance' | 'integratedDocument') => async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (file.size > maxSize) {
        setModalAlertMessage({
          type: 'error',
          message: 'File size must be less than 5MB'
        });
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setModalAlertMessage({
          type: 'error',
          message: 'Only JPEG, PNG, and PDF files are allowed'
        });
        return;
      }

      setDocuments({ ...documents, [docType]: file });
      setErrors(prev => ({ ...prev, [docType]: '' }));
      setModalAlertMessage(null); // Clear any previous errors

      // Extract information from document using AI
      if (!editingVehicle) { // Only extract for new vehicles
        setExtractingDocInfo(docType);
        setLoading(true, 'Extracting information from document...');

        try {
          const formData = new FormData();
          formData.append('document', file);

          // Determine document type for AI extraction
          let aiDocumentType = 'VEHICLE_REGISTRATION';
          if (docType === 'insurance') {
            aiDocumentType = 'INSURANCE_CERTIFICATE';
          } else if (docType === 'integratedDocument') {
            aiDocumentType = 'VEHICLE_REGISTRATION'; // Integrated documents contain registration info
          } else if (docType === 'vehicleRegistration') {
            aiDocumentType = 'VEHICLE_REGISTRATION';
          }

          formData.append('documentType', aiDocumentType);

          console.log('📤 Sending document to AI for extraction:', docType, '-> AI type:', aiDocumentType);
          const response = await apiService.uploadFile('/vehicles/extract-document-info', formData);

          console.log('📥 Extract document response:', response);

          if (response.success && response.data) {
            const extractedData = response.data;

            // Check if AI detected wrong document type (error field)
            if (extractedData.error) {
              console.error('❌ Wrong document type detected:', extractedData.error);
              setModalAlertMessage({
                type: 'error',
                message: extractedData.error || 'Wrong document type detected. Please upload the correct document.'
              });
              // Clear the uploaded file
              setDocuments({ ...documents, [docType]: null });
              return;
            }

            // Show dialog for user to confirm or edit extracted data
            if ((docType === 'vehicleRegistration' || docType === 'integratedDocument') && extractedData) {
              // Prepare editable data structure
              const editableData = {
                licensePlate: extractedData.licensePlate || '',
                color: extractedData.color || '',
                year: extractedData.year ? extractedData.year.toString() : '',
                make: extractedData.make || '',
                model: extractedData.model || '',
                vin: extractedData.vin || '',
                ownerName: extractedData.ownerName || '',
                province: extractedData.province || '',
                expiryDate: extractedData.expiryDate || ''
              };

              console.log('✅ Extracted data:', editableData);
              console.log('🔓 Opening extracted data dialog...');

              setExtractedData(extractedData);
              setEditableExtractedData(editableData);
              setDataWasModifiedByUser(false); // Reset modification flag
              setExtractedDataDialogOpen(true);

              setModalAlertMessage({
                type: 'success',
                message: 'Vehicle information extracted successfully! Please review and confirm the details.'
              });
            } else if (docType === 'insurance' && extractedData) {
              // Insurance data can be used for validation but doesn't auto-fill vehicle info
              setModalAlertMessage({
                type: 'success',
                message: 'Insurance document processed successfully! Please upload vehicle registration to extract vehicle details.'
              });
            } else {
              console.warn('⚠️ Unexpected response structure:', extractedData);
              setModalAlertMessage({
                type: 'warning',
                message: 'Document uploaded but could not extract information automatically. Please fill in the details manually.'
              });
              setManualMode(true);
            }
          } else {
            const errorMessage = response.message || 'Document uploaded but could not extract information automatically.';
            console.error('❌ Extract failed - response:', response);
            setModalAlertMessage({
              type: 'warning',
              message: `${errorMessage} Please fill in the vehicle details manually.`
            });
            setManualMode(true);
          }
        } catch (error: any) {
          console.error('❌ Extract document info error:', error);
          console.error('❌ Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            fullError: error
          });

          // Extract error message from various possible locations
          let errorMessage = 'Could not extract information automatically. Please try again.';

          if (error.response?.data) {
            // Check if data is an object with message property
            if (typeof error.response.data === 'object' && error.response.data.message) {
              errorMessage = error.response.data.message;
            }
            // Check if data is a string (JSON stringified or plain message)
            else if (typeof error.response.data === 'string') {
              try {
                const parsed = JSON.parse(error.response.data);
                errorMessage = parsed.message || errorMessage;
              } catch {
                // If not JSON, use the string itself as the message
                errorMessage = error.response.data;
              }
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          console.error('📢 Displaying error to user:', errorMessage);
          setModalAlertMessage({
            type: 'warning',
            message: `${errorMessage} Please fill in the vehicle details manually.`
          });
          setManualMode(true);
        } finally {
          setExtractingDocInfo(null);
          setLoading(false);
        }
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ flexGrow: 1, p: { xs: 1, md: 3 }, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, md: 3 } }}>
      <Container maxWidth="lg">
        {/* Alert Message */}
        {alertMessage && (
          <Alert
            severity={alertMessage.type}
            sx={{ mb: 3, '& .MuiAlert-message': { fontSize: '1.5rem' } }}
            onClose={() => setAlertMessage(null)}
          >
            {alertMessage.message}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                mb: 1,
                fontSize: { xs: '2.25rem', md: '3rem' }
              }}
            >
              🚗 Vehicle Management
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.5rem' }}>
              Manage your vehicles for ride posting
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddVehicle}
            sx={{ minWidth: { xs: 'auto', md: 140 }, fontSize: '1.5rem' }}
          >
            {isMobile ? 'Add' : 'Add Vehicle'}
          </Button>
        </Box>

        {/* Vehicles List */}
        {vehicles.length === 0 ? (
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
            <CarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1.5rem' }}>
              No vehicles added yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: '1.5rem' }}>
              Add your first vehicle to start posting rides
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddVehicle}
              sx={{ fontSize: '1.5rem' }}
            >
              Add Your First Vehicle
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
            {vehicles.map((vehicle) => (
              <Box key={vehicle.id}>
                <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Verification Status Badge */}
                    {(() => {
                      const statusDisplay = getVerificationStatusDisplay(vehicle.verificationStatus);
                      return (
                        <Chip
                          icon={statusDisplay.icon}
                          label={statusDisplay.label}
                          size="small"
                          color={statusDisplay.color}
                          sx={{ mb: 2, fontSize: '1.5rem' }}
                        />
                      );
                    })()}

                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', fontSize: '1.5rem' }}>
                      {vehicle.year} {vehicle.make.name} {vehicle.model.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '1.5rem' }}>
                      {t(`vehicle.colorNames.${vehicle.color}`, vehicle.color)} • {vehicle.licensePlate} • {vehicle.seats} seats
                    </Typography>

                    <Chip
                      label={vehicle.type}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 2, fontSize: '1.5rem' }}
                    />

                    {/* Admin Note (shown for rejected/needs update) */}
                    {vehicle.adminNote && (vehicle.verificationStatus === 'REJECTED' || vehicle.verificationStatus === 'NEEDS_UPDATE') && (
                      <Alert severity={vehicle.verificationStatus === 'REJECTED' ? 'error' : 'warning'} sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
                        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '1.125rem' }}>Admin Note:</Typography>
                        <Typography variant="body2" sx={{ fontSize: '1.5rem' }}>{vehicle.adminNote}</Typography>
                      </Alert>
                    )}

                    {vehicle.photos && vehicle.photos.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box
                          component="img"
                          src={`${FILE_BASE_URL}${vehicle.photos[0]}`}
                          alt={`${vehicle.make.name} ${vehicle.model.name}`}
                          sx={{
                            width: '100%',
                            maxHeight: 180,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        />
                      </Box>
                    )}

                    {vehicle.features && vehicle.features.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', fontSize: '1.5rem' }}>
                          Features:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {vehicle.features.slice(0, 3).map((featureId) => {
                            const feature = getFeatureDisplay(featureId);
                            return feature ? (
                              <Chip
                                key={featureId}
                                icon={feature.icon}
                                label={feature.label}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '1.5rem' }}
                              />
                            ) : null;
                          })}
                          {vehicle.features.length > 3 && (
                            <Chip
                              label={`+${vehicle.features.length - 3} more`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '1.5rem' }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    {vehicle.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.5rem' }}>
                        {vehicle.description}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
                    {/* Resubmit button for rejected/needs update vehicles */}
                    {(vehicle.verificationStatus === 'REJECTED' || vehicle.verificationStatus === 'NEEDS_UPDATE') && (
                      <Button
                        fullWidth
                        variant="contained"
                        color="warning"
                        startIcon={resubmitting === vehicle.id ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                        onClick={() => handleResubmitVehicle(vehicle.id)}
                        disabled={resubmitting === vehicle.id}
                        size="small"
                        sx={{ fontSize: '1.5rem' }}
                      >
                        {resubmitting === vehicle.id ? 'Resubmitting...' : 'Resubmit for Review'}
                      </Button>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Button
                        startIcon={<EditIcon />}
                        onClick={() => handleEditVehicle(vehicle)}
                        size="small"
                        sx={{ fontSize: '1.5rem' }}
                      >
                        Edit
                      </Button>
                      <Button
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteVehicle(vehicle)}
                        color="error"
                        size="small"
                        sx={{ fontSize: '1.5rem' }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        )}

        {/* Add/Edit Vehicle Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setModalAlertMessage(null); // Clear modal alert when closing
          }}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontSize: '1.5rem' }}>
              {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </Typography>
            <IconButton onClick={() => {
              setDialogOpen(false);
              setModalAlertMessage(null);
            }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Modal Alert Message for Document Upload Errors */}
              {modalAlertMessage && (
                <Alert
                  severity={modalAlertMessage.type}
                  onClose={() => setModalAlertMessage(null)}
                  sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '1.5rem' } }}
                >
                  {modalAlertMessage.message}
                </Alert>
              )}

              {/* Vehicle Photo Section - At the top */}
              <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.5rem' }}>
                  <PhotoCameraIcon />
                  Vehicle Photo (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '1.5rem' }}>
                  Add a photo of your vehicle to help passengers identify it. Photos are checked for appropriate content.
                </Typography>

                {/* Show existing photos when editing */}
                {editingVehicle && editingVehicle.photos && editingVehicle.photos.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '1.5rem' }}>Current Photos:</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {editingVehicle.photos.map((photo, index) => (
                        <Box key={index} sx={{ position: 'relative' }}>
                          <img
                            src={`${FILE_BASE_URL}${photo}`}
                            alt={`Vehicle ${index + 1}`}
                            style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 8 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteVehiclePhoto(editingVehicle.id, photo)}
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'error.dark' },
                              width: 24,
                              height: 24
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Photo preview */}
                {vehiclePhotoPreview && (
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                      src={vehiclePhotoPreview}
                      alt="Preview"
                      style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8 }}
                    />
                    <IconButton
                      onClick={() => {
                        setVehiclePhoto(null);
                        setVehiclePhotoPreview(null);
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AddPhotoIcon />}
                    disabled={uploadingPhoto}
                    sx={{ fontSize: '1.5rem' }}
                  >
                    Select Photo
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleVehiclePhotoChange}
                    />
                  </Button>

                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    disabled={uploadingPhoto}
                    sx={{ fontSize: '1.5rem' }}
                  >
                    Take Photo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      capture={isMobile ? "environment" : undefined}
                      onChange={handleVehiclePhotoChange}
                    />
                  </Button>

                  {vehiclePhoto && editingVehicle && (
                    <Button
                      variant="contained"
                      onClick={() => handleUploadVehiclePhoto(editingVehicle.id)}
                      disabled={uploadingPhoto}
                      startIcon={uploadingPhoto ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                      sx={{ fontSize: '1.5rem' }}
                    >
                      {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                    </Button>
                  )}
                </Box>

                {!editingVehicle && vehiclePhoto && (
                  <Alert severity="info" sx={{ mt: 2, '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
                    Photo will be uploaded after the vehicle is created.
                  </Alert>
                )}
              </Paper>
              
              <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.5rem' }}>
                <CarIcon />
                Vehicle Information
              </Typography>

              {!editingVehicle && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCustomVehicle}
                      onChange={(e) => {
                        setIsCustomVehicle(e.target.checked);
                        // Reset form values for make/model
                        setVehicleForm(prev => ({
                          ...prev,
                          makeId: '',
                          makeName: '',
                          modelId: '',
                          modelName: '',
                          customMake: '',
                          customModel: '',
                          type: 'SEDAN',
                          seats: 5
                        }));
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '1.25rem' }}>Can't find your car brand/model? Enter manually</Typography>}
                  sx={{ mb: 1 }}
                />
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
                {isCustomVehicle ? (
                  <>
                    <Box>
                      <TextField
                        fullWidth
                        label="Make *"
                        value={vehicleForm.customMake || ''}
                        onChange={(e) => setVehicleForm({ ...vehicleForm, customMake: e.target.value })}
                        error={!!errors.makeId}
                        helperText={errors.makeId}
                        placeholder="e.g. Fiat, Peugeot, Lada..."
                        InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                        inputProps={{ sx: { fontSize: '1.5rem' } }}
                        FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                      />
                    </Box>
                    <Box>
                      <TextField
                        fullWidth
                        label="Model *"
                        value={vehicleForm.customModel || ''}
                        onChange={(e) => setVehicleForm({ ...vehicleForm, customModel: e.target.value })}
                        error={!!errors.modelId}
                        helperText={errors.modelId}
                        placeholder="e.g. 500, 208, Niva..."
                        InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                        inputProps={{ sx: { fontSize: '1.5rem' } }}
                        FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                      />
                    </Box>
                  </>
                ) : (
                  <>
                    <Box>
                      <Autocomplete
                        value={vehicleMakes.find(m => m.id === vehicleForm.makeId) || null}
                        onChange={(_, value) => {
                          if (value) {
                            handleMakeChange(value.id, value.name);
                          } else {
                            handleMakeChange('', '');
                          }
                        }}
                        options={vehicleMakes}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Make *"
                            error={!!errors.makeId}
                            helperText={errors.makeId}
                            placeholder="Toyota, Honda, Ford..."
                            InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                            inputProps={{
                              ...params.inputProps,
                              sx: { fontSize: '1.5rem' }
                            }}
                            FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                          />
                        )}
                      />
                    </Box>
                    <Box>
                      <Autocomplete
                        value={vehicleModels.find(m => m.id === vehicleForm.modelId) || null}
                        onChange={(_, value) => {
                          if (value) {
                            handleModelChange(value.id, value.name, value.type, value.defaultSeats);
                          } else {
                            handleModelChange('', '', 'SEDAN', 5);
                          }
                        }}
                        options={vehicleModels}
                        getOptionLabel={(option) => option.name}
                        disabled={!vehicleForm.makeId}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Model *"
                            error={!!errors.modelId}
                            helperText={errors.modelId}
                            placeholder="Camry, Civic, F-150..."
                            InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                            inputProps={{
                              ...params.inputProps,
                              sx: { fontSize: '1.5rem' }
                            }}
                            FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                          />
                        )}
                      />
                    </Box>
                  </>
                )}

                <Box>
                  <TextField
                    fullWidth
                    label="Year *"
                    value={vehicleForm.year}
                    onChange={handleFormChange('year')}
                    error={!!errors.year}
                    helperText={errors.year}
                    placeholder="2020"
                    type="number"
                    inputProps={{ min: 1900, max: new Date().getFullYear() + 1, sx: { fontSize: '1.5rem' } }}
                    InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                    FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                  />
                </Box>

                <Box>
                  <FormControl fullWidth error={!!errors.color}>
                    <InputLabel sx={{ fontSize: '1.5rem' }}>Color *</InputLabel>
                    <Select
                      value={vehicleForm.color}
                      onChange={handleFormChange('color')}
                      label="Color *"
                      sx={{ fontSize: '1.5rem' }}
                    >
                      {VEHICLE_COLORS.map((color) => (
                        <MenuItem key={color} value={color} sx={{ fontSize: '1.5rem' }}>
                          {color}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3, mt: 3 }}>
                <Box>
                  <TextField
                    fullWidth
                    label="License Plate *"
                    value={vehicleForm.licensePlate}
                    onChange={handleFormChange('licensePlate')}
                    error={!!errors.licensePlate}
                    helperText={errors.licensePlate}
                    placeholder="ABC123"
                    inputProps={{ style: { textTransform: 'uppercase', fontSize: '1.5rem' } }}
                    InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                    FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                  />
                </Box>

                <Box>
                  {isCustomVehicle ? (
                    <FormControl fullWidth>
                      <InputLabel sx={{ fontSize: '1.5rem' }}>Vehicle Type *</InputLabel>
                      <Select
                        value={vehicleForm.type}
                        onChange={(e) => {
                          const newType = e.target.value;
                          const defaultSeatsMap: { [key: string]: number } = {
                            SEDAN: 5,
                            SUV: 7,
                            VAN: 8,
                            TRUCK: 5,
                            COUPE: 4,
                            HATCHBACK: 5
                          };
                          setVehicleForm(prev => ({
                            ...prev,
                            type: newType,
                            seats: defaultSeatsMap[newType] || 5
                          }));
                        }}
                        label="Vehicle Type *"
                        sx={{ fontSize: '1.5rem' }}
                      >
                        {['SEDAN', 'SUV', 'VAN', 'TRUCK', 'COUPE', 'HATCHBACK'].map((type) => (
                          <MenuItem key={type} value={type} sx={{ fontSize: '1.5rem' }}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label="Vehicle Type"
                      value={vehicleForm.type}
                      disabled
                      helperText="Auto-determined by model"
                      InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                      inputProps={{ sx: { fontSize: '1.5rem' } }}
                    />
                  )}
              </Box>
            </Box>

            <Box sx={{ mt: 3 }}>
                {(() => {
                  const maxSeatsMap: { [key: string]: number } = {
                    SEDAN: 5,
                    SUV: 8,
                    VAN: 15,
                    TRUCK: 5,
                    COUPE: 4,
                    HATCHBACK: 5
                  };
                  const currentMax = maxSeatsMap[vehicleForm.type] || 5;
                  if (vehicleForm.seats > currentMax) {
                    setTimeout(() => {
                      setVehicleForm(prev => ({ ...prev, seats: currentMax }));
                    }, 0);
                  }
                  return (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 1 }}>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
                          Available Seats:
                        </Typography>
                        <Chip
                          icon={<ChairIcon sx={{ '&&': { color: 'inherit' } }} />}
                          label={`${vehicleForm.seats} Seats`}
                          color="primary"
                          sx={{ fontSize: '1.4rem', fontWeight: 'bold', p: 1.8 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1.5, borderRadius: 2, mb: 3, justifyContent: 'center', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                        {Array.from({ length: vehicleForm.seats }).map((_, idx) => (
                          <ChairIcon key={idx} sx={{ fontSize: '2.2rem', color: theme.palette.primary.main }} />
                        ))}
                        {Array.from({ length: currentMax - vehicleForm.seats }).map((_, idx) => (
                          <ChairIcon key={idx} sx={{ fontSize: '2.2rem', color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }} />
                        ))}
                      </Box>
                      <Slider
                        value={vehicleForm.seats}
                        onChange={(_, value) => handleFormChange('seats')(value)}
                        min={1}
                        max={currentMax}
                        marks
                        valueLabelDisplay="on"
                        sx={{ mt: 2 }}
                      />
                    </>
                  );
                })()}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => {
              setDialogOpen(false);
              setModalAlertMessage(null);
            }} startIcon={<CancelIcon />} sx={{ fontSize: '1.5rem' }}>
              Cancel
            </Button>
            {editingVehicle ? (
              <Button
                onClick={handleSaveVehicle}
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{ fontSize: '1.5rem' }}
              >
                Update Vehicle
              </Button>
            ) : (
              <Button
                onClick={handleSaveVehicle}
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isConfirmingVehicle}
                sx={{ fontSize: '1.5rem' }}
              >
                {isConfirmingVehicle ? 'Saving...' : 'Save Vehicle'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Onboarding Add Vehicle Dialog */}
        <Dialog 
          open={onboardingModalOpen} 
          onClose={() => setOnboardingModalOpen(false)}
          scroll="paper"
          sx={{
            '& .MuiDialog-container': {
              alignItems: 'flex-start',
              paddingTop: '100px',
            }
          }}
          PaperProps={{ sx: { p: 2, borderRadius: 3, maxWidth: 500 } }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.875rem' }}>
              Identity Verified!
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2, textAlign: 'center', fontSize: '1.3125rem', lineHeight: 1.5 }}>
              Your driver identity is verified! The final step is to add your vehicle details to start offering rides.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 2, alignItems: 'stretch', px: 3, pb: 3 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                setOnboardingModalOpen(false);
                handleAddVehicle();
              }}
              sx={{ py: 1.5, fontSize: '1.25rem', textTransform: 'none', borderRadius: '8px' }}
            >
              Add My Vehicle
            </Button>
            <Button
              variant="text"
              onClick={() => setOnboardingModalOpen(false)}
              sx={{ color: 'text.secondary', fontSize: '1.1rem', textTransform: 'none' }}
            >
              Later
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle sx={{ fontSize: '1.5rem' }}>Delete Vehicle</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '1.5rem' }}>
              Are you sure you want to delete your {vehicleToDelete?.year} {vehicleToDelete?.make.name} {vehicleToDelete?.model.name}?
            </Typography>
            <Alert severity="warning" sx={{ mt: 2, '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
              This action cannot be undone. Any rides using this vehicle will need to be updated.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontSize: '1.5rem' }}>
              Cancel
            </Button>
            <Button onClick={confirmDeleteVehicle} color="error" variant="contained" sx={{ fontSize: '1.5rem' }}>
              Delete Vehicle
            </Button>
          </DialogActions>
        </Dialog>

        {/* Extracted Data Confirmation Dialog */}
        <Dialog
          open={extractedDataDialogOpen}
          onClose={() => {
            setExtractedDataDialogOpen(false);
            setExtractedData(null);
            setEditableExtractedData(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.5rem' }}>
            <CarIcon color="primary" />
            Review Extracted Vehicle Information
          </DialogTitle>
          <DialogContent dividers>
            <Alert severity={dataWasModifiedByUser ? "warning" : "success"} sx={{ mb: 3, '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
              <Box sx={{ mt: 3 }}>
                {(() => {
                  const maxSeatsMap: { [key: string]: number } = {
                    SEDAN: 5,
                    SUV: 8,
                    VAN: 15,
                    TRUCK: 5,
                    COUPE: 4,
                    HATCHBACK: 5
                  };
                  const currentMax = maxSeatsMap[vehicleForm.type] || 5;
                  // If current seats value exceeds the vehicle type maximum, cap it
                  if (vehicleForm.seats > currentMax) {
                    setTimeout(() => {
                      setVehicleForm(prev => ({ ...prev, seats: currentMax }));
                    }, 0);
                  }
                  return (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 1 }}>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
                          Available Seats:
                        </Typography>
                        <Chip
                          icon={<ChairIcon sx={{ '&&': { color: 'inherit' } }} />}
                          label={`${vehicleForm.seats} Seats`}
                          color="primary"
                          sx={{ fontSize: '1.4rem', fontWeight: 'bold', p: 1.8 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1.5, borderRadius: 2, mb: 3, justifyContent: 'center', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                        {Array.from({ length: vehicleForm.seats }).map((_, idx) => (
                          <ChairIcon key={idx} sx={{ fontSize: '2.2rem', color: theme.palette.primary.main }} />
                        ))}
                        {Array.from({ length: currentMax - vehicleForm.seats }).map((_, idx) => (
                          <ChairIcon key={idx} sx={{ fontSize: '2.2rem', color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }} />
                        ))}
                      </Box>
                      <Slider
                        value={vehicleForm.seats}
                        onChange={(_, value) => handleFormChange('seats')(value)}
                        min={1}
                        max={currentMax}
                        marks
                        valueLabelDisplay="on"
                        sx={{ mt: 2 }}
                      />
                    </>
                  );
                })()}
              </Box>
              <Typography variant="body2" fontWeight="bold" gutterBottom sx={{ fontSize: '1.5rem' }}>
                {dataWasModifiedByUser
                  ? "⚠️ Data Modified - Admin Verification Required"
                  : "✅ AI Verified - Ready for Auto-Approval"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '1.5rem' }}>
                {dataWasModifiedByUser
                  ? "You've modified the AI-extracted data. Your vehicle will be sent to admin for verification before you can create rides."
                  : "AI has extracted and verified the information. If you confirm without changes, your vehicle will be auto-approved and ready to use immediately!"}
              </Typography>
            </Alert>

            {editableExtractedData && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="License Plate"
                  value={editableExtractedData.licensePlate || ''}
                  onChange={(e) => handleEditExtractedData('licensePlate', e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                  inputProps={{ sx: { fontSize: '1.5rem' } }}
                />

                <TextField
                  label="Color"
                  value={editableExtractedData.color || ''}
                  onChange={(e) => handleEditExtractedData('color', e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                  inputProps={{ sx: { fontSize: '1.5rem' } }}
                />

                <TextField
                  label="Year"
                  type="number"
                  value={editableExtractedData.year || ''}
                  onChange={(e) => handleEditExtractedData('year', e.target.value)}
                  fullWidth
                  required
                  inputProps={{ min: 1990, max: new Date().getFullYear() + 1, sx: { fontSize: '1.5rem' } }}
                  InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                />

                <TextField
                  label="Make"
                  value={editableExtractedData.make || ''}
                  onChange={(e) => handleEditExtractedData('make', e.target.value)}
                  fullWidth
                  required
                  helperText="AI will try to match this with available makes"
                  InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                  inputProps={{ sx: { fontSize: '1.5rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                />

                <TextField
                  label="Model"
                  value={editableExtractedData.model || ''}
                  onChange={(e) => handleEditExtractedData('model', e.target.value)}
                  fullWidth
                  required
                  helperText="AI will try to match this with available models"
                  InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                  inputProps={{ sx: { fontSize: '1.5rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                />

                <TextField
                  label="VIN (Optional)"
                  value={editableExtractedData.vin || ''}
                  onChange={(e) => handleEditExtractedData('vin', e.target.value)}
                  fullWidth
                  InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                  inputProps={{ sx: { fontSize: '1.5rem' } }}
                />

                {editableExtractedData.ownerName && (
                  <TextField
                    label="Owner Name"
                    value={editableExtractedData.ownerName || ''}
                    disabled
                    fullWidth
                    helperText="From registration document"
                    InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                    inputProps={{ sx: { fontSize: '1.5rem' } }}
                    FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                  />
                )}

                {editableExtractedData.province && (
                  <TextField
                    label="Province"
                    value={editableExtractedData.province || ''}
                    disabled
                    fullWidth
                    helperText="From registration document"
                    InputLabelProps={{ sx: { fontSize: '1.5rem' } }}
                    inputProps={{ sx: { fontSize: '1.5rem' } }}
                    FormHelperTextProps={{ sx: { fontSize: '1.5rem' } }}
                  />
                )}
              </Box>
            )}


          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => {
                setExtractedDataDialogOpen(false);
                setExtractedData(null);
                setEditableExtractedData(null);
              }}
              startIcon={<CancelIcon />}
              sx={{ fontSize: '1.5rem' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmExtractedData}
              variant="contained"
              color={dataWasModifiedByUser ? "warning" : "success"}
              startIcon={<SaveIcon />}
              disabled={!editableExtractedData?.licensePlate || !editableExtractedData?.color || !editableExtractedData?.year || isConfirmingVehicle}
              sx={{ fontSize: '1.5rem' }}
            >
              {isConfirmingVehicle
                ? "Processing..."
                : dataWasModifiedByUser
                  ? "Confirm & Send for Verification"
                  : "Confirm & Auto-Approve Vehicle"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default VehicleManagementPage;